import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Bell, Menu, ChevronDown, Command, TrendingUp } from "lucide-react";
import api from "@/lib/api";

const CATS = ["Latest", "Web3", "DeFi", "Blockchain", "Crypto", "Policy", "Markets"];
const SUBS: Record<string, string[]> = {
  Web3: ["NFTs & Gaming", "Metaverse", "DAOs", "Identity", "All Web3 →"],
  DeFi: ["Protocols", "Yield & Staking", "Stablecoins", "Exploits", "All DeFi →"],
  Crypto: ["Bitcoin", "Ethereum", "Altcoins", "Mining", "All Crypto →"],
  Policy: ["US Policy", "EU / MiCA", "Asia", "Enforcement", "All Policy →"],
  Markets: ["Live Prices", "ETFs & Funds", "Derivatives", "Analysis", "All Markets →"],
  Blockchain: ["Layer 1s", "Layer 2s", "Infrastructure", "Enterprise", "All Blockchain →"],
  Latest: ["Breaking", "Most Read", "Deep Dives", "Weekly Recap", "All Stories →"],
};

const FakeLogo = () => <img src="/logo.png" alt="NewsLayer" className="h-8 w-auto max-w-none shrink-0" />;

const MiniCard = ({ a }: { a: any }) => (
  <a className="flex gap-3 group cursor-pointer">
    <img src={a?.hero_image_url} alt="" className="w-20 h-14 object-cover rounded-md border border-white/10 shrink-0" />
    <p className="text-sm text-gray-300 group-hover:text-white leading-snug line-clamp-3 transition-colors">{a?.title}</p>
  </a>
);

/* ── Option 1: Broadcast Two-Tier ─────────────────────────── */
const Option1 = () => {
  const [active, setActive] = useState("Latest");
  return (
    <div className="bg-black border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-14">
        <FakeLogo />
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-400" />
          <Bell className="h-4 w-4 text-gray-400 ml-3" />
          <button className="ml-4 w-[96px] border border-[#7C3AED] text-white text-sm font-bold rounded-md py-1.5">Sign in</button>
          <button className="w-[96px] bg-[#7C3AED] text-white text-sm font-bold rounded-md py-1.5">Subscribe</button>
        </div>
      </div>
      <div className="border-t border-white/10 bg-[#0c0c0c]">
        <div className="max-w-7xl mx-auto px-6 flex items-stretch">
          <span className="flex items-center gap-1.5 pr-5 border-r border-white/10 my-2">
            <span className="w-2 h-2 rounded-full bg-[#DC2626] animate-pulse" />
            <span className="font-heading font-black text-[11px] tracking-[0.2em] text-white">LIVE</span>
          </span>
          {CATS.map((c) => (
            <button key={c} onMouseEnter={() => setActive(c)}
              className={`relative px-4 py-3 font-heading font-bold text-[12px] tracking-[0.15em] uppercase transition-colors ${
                active === c ? "text-white" : "text-gray-500 hover:text-gray-200"}`}>
              {c}
              {active === c && <span className="absolute bottom-0 inset-x-3 h-[3px] bg-[#DC2626]" />}
            </button>
          ))}
          <span className="ml-auto flex items-center">
            <button className="bg-[#DC2626] text-white font-heading font-black text-[11px] tracking-[0.2em] uppercase px-4 py-1.5 rounded-sm">Breaking</button>
          </span>
        </div>
      </div>
    </div>
  );
};

/* ── Option 2: Mega Menu ──────────────────────────────────── */
const Option2 = ({ articles }: { articles: any[] }) => {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="relative bg-black/90 border-b border-white/10" onMouseLeave={() => setOpen(null)}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        <FakeLogo />
        <nav className="flex items-center">
          {CATS.map((c) => (
            <button key={c} onMouseEnter={() => setOpen(c)}
              className={`flex items-center gap-1 px-3.5 py-5 text-sm font-medium transition-colors ${
                open === c ? "text-[#00E5FF]" : "text-gray-300 hover:text-white"}`}>
              {c} <ChevronDown className={`h-3 w-3 transition-transform ${open === c ? "rotate-180" : ""}`} />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-400" />
          <button className="w-[96px] bg-[#7C3AED] text-white text-sm font-bold rounded-md py-1.5 ml-3">Subscribe</button>
        </div>
      </div>
      {open && (
        <div className="absolute inset-x-0 top-full bg-[#0d0d0d]/98 backdrop-blur-xl border-b border-white/10 shadow-2xl shadow-black/70 animate-in fade-in slide-in-from-top-1 duration-200 z-20">
          <div className="max-w-7xl mx-auto px-6 py-7 grid grid-cols-[220px_1px_1fr] gap-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] mb-3">{open}</p>
              <ul className="space-y-2.5">
                {(SUBS[open] || []).map((s) => (
                  <li key={s} className={`text-sm cursor-pointer transition-colors ${
                    s.includes("→") ? "text-[#00E5FF] font-bold mt-4" : "text-gray-400 hover:text-white"}`}>{s}</li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10" />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-3">Trending in {open}</p>
              <div className="grid grid-cols-2 gap-5">
                {articles.slice(0, 4).map((a, i) => <MiniCard key={i} a={a} />)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Option 3: Terminal Command Bar ───────────────────────── */
const Option3 = () => {
  const [active, setActive] = useState("Latest");
  return (
    <div className="bg-[#050505] border-b border-[#00E5FF]/20">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 gap-4">
        <FakeLogo />
        <nav className="flex items-center gap-1 bg-white/[0.04] border border-white/10 rounded-full px-1.5 py-1.5">
          {CATS.map((c) => (
            <button key={c} onMouseEnter={() => setActive(c)}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-all ${
                active === c
                  ? "bg-[#00E5FF]/15 text-[#00E5FF] shadow-[0_0_12px_rgba(0,229,255,0.25)] border border-[#00E5FF]/40"
                  : "text-gray-400 hover:text-white border border-transparent"}`}>
              {c}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <span className="hidden xl:flex items-center gap-1.5 font-mono text-xs bg-white/[0.04] border border-white/10 rounded-md px-2.5 py-1.5">
            <TrendingUp className="h-3 w-3 text-[#4ADE80]" />
            <span className="text-white">BTC $64.9K</span>
            <span className="text-[#4ADE80]">+0.1%</span>
          </span>
          <button className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-md px-3 py-1.5 text-gray-500 text-xs">
            <Search className="h-3.5 w-3.5" /> Search
            <span className="flex items-center gap-0.5 border border-white/15 rounded px-1 text-[10px]"><Command className="h-2.5 w-2.5" />K</span>
          </button>
          <button className="bg-[#7C3AED] text-white text-sm font-bold rounded-md px-4 py-1.5">Subscribe</button>
        </div>
      </div>
    </div>
  );
};

/* ── Option 4: Editorial Centered ─────────────────────────── */
const Option4 = () => {
  const [active, setActive] = useState("Latest");
  return (
    <div className="bg-[#080808] border-b-2 border-white/15">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-3 items-center h-16">
        <div className="flex items-center gap-3 font-mono text-[11px] text-gray-500 uppercase tracking-wider">
          <span>Sat, Jun 20 2026</span>
          <span className="hidden xl:inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80]" /> Markets Open</span>
        </div>
        <div className="flex justify-center"><img src="/logo.png" alt="NewsLayer" className="h-10 w-auto max-w-none" /></div>
        <div className="flex items-center justify-end gap-2">
          <Search className="h-4 w-4 text-gray-400 mr-1" />
          <button className="text-sm text-gray-300 font-medium px-2">Sign in</button>
          <button className="bg-[#7C3AED] text-white text-sm font-bold rounded-md px-4 py-1.5">Subscribe</button>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          {CATS.map((c, i) => (
            <button key={c} onMouseEnter={() => setActive(c)}
              className={`relative px-5 py-2.5 text-[12px] uppercase tracking-[0.22em] transition-colors ${
                i > 0 ? "border-l border-white/[0.07]" : ""} ${
                active === c ? "text-white" : "text-gray-500 hover:text-gray-200"}`}>
              {active === c && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[2px] bg-[#00E5FF]" />}
              {c}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Page ─────────────────────────────────────────────────── */
const Block = ({ n, title, sub, children }: any) => (
  <section className="mb-14" data-testid={`header-option-${n}`}>
    <div className="max-w-7xl mx-auto px-6 mb-4">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00E5FF]">Option {n}</p>
      <h2 className="font-heading font-bold text-white text-lg md:text-lg mt-1">{title}</h2>
      <p className="text-gray-500 text-sm mt-1 max-w-2xl">{sub}</p>
    </div>
    <div className="border-y border-white/5">{children}</div>
    <div className="h-16 bg-gradient-to-b from-white/[0.02] to-transparent" />
  </section>
);

export default function HeaderPreview() {
  const { data } = useQuery({
    queryKey: ["homepage"],
    queryFn: async () => (await api.get("/homepage")).data,
  });
  const articles = data?.latest || [];
  return (
    <div className="py-10" data-testid="header-preview-page">
      <div className="max-w-7xl mx-auto px-6 mb-10">
        <h1 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl">Header Options</h1>
        <p className="text-gray-400 mt-3 max-w-2xl">
          Four directions for the main navigation. Hover the tabs in each one — Option 2's mega menu opens on hover.
          Tell me the number and I'll build it into the real site (desktop + mobile).
        </p>
      </div>
      <Block n={1} title="Broadcast Two-Tier" sub="Fox News / Bloomberg energy — dedicated red category band with a pulsing LIVE badge and a Breaking shortcut. Loud, newsy, unmistakable.">
        <Option1 />
      </Block>
      <Block n={2} title="Mega Menu" sub="CoinDesk / The Block style — hover any section for a rich panel with sub-topics and trending stories. Feels like a big-league publication.">
        <Option2 articles={articles} />
      </Block>
      <Block n={3} title="Terminal Command Bar" sub="Trading-terminal aesthetic — glowing pill tabs, a live BTC chip, and a ⌘K search. Modern, techy, very crypto-native.">
        <Option3 />
      </Block>
      <Block n={4} title="Editorial Centered" sub="Premium newspaper feel — centered masthead with date and market status, thin-ruled category row beneath. Understated and authoritative.">
        <Option4 />
      </Block>
    </div>
  );
}
